import {useEffect, useRef, useState} from 'react'
import type {ChangesetFile} from '@microsoft/agent-host-protocol'
import type {EstablishedAhpSessionView} from '@github-ui/ahp-session-client/contracts'

import {resolveChangesetFileContent, type ResolvedChangesetFileContent} from '../utils/resolve-changeset-file-content'

/** Bounds how many `readResource` calls are in flight at once so a large changeset doesn't fire
 * dozens of concurrent requests on the shared relay connection. */
const MAX_CONCURRENT_READS = 4

export type ChangesetDiffContentStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable'

export interface UseChangesetDiffContentResult {
  readonly status: ChangesetDiffContentStatus
  readonly contentByFileId: ReadonlyMap<string, ResolvedChangesetFileContent>
}

const EMPTY_CONTENT: ReadonlyMap<string, ResolvedChangesetFileContent> = new Map()
const READY_EMPTY_RESULT: UseChangesetDiffContentResult = {status: 'ready', contentByFileId: EMPTY_CONTENT}
const IDLE_RESULT: UseChangesetDiffContentResult = {status: 'idle', contentByFileId: EMPTY_CONTENT}

/** Whether a file's *content* needs a fresh `readResource` — only its before/after content URIs
 * matter, not its path or host-reported counts (see {@link revisionToken}). */
function fileContentKey(file: ChangesetFile): string {
  // `content.uri` alone can be stable across edits (some hosts point it at "current content for
  // this file" rather than a per-revision address) — `content.nonce` is the protocol's dedicated
  // cache-busting token for exactly this case, so it must be part of the key too, or a file edited
  // twice in a row would keep showing its first edit's cached content forever.
  const before = file.edit.before?.content
  const after = file.edit.after?.content
  return `${file.id}:${before?.uri ?? ''}:${before?.nonce ?? ''}:${after?.uri ?? ''}:${after?.nonce ?? ''}`
}

/** Identifies a changeset's full displayable+resolvable state, so the resolution effect only
 * re-runs when something meaningful (not just render) actually changed. */
function revisionToken(files: readonly ChangesetFile[]): string {
  return files
    .map(file =>
      [
        fileContentKey(file),
        file.edit.before?.uri ?? '',
        file.edit.after?.uri ?? '',
        file.edit.diff?.added ?? '',
        file.edit.diff?.removed ?? '',
      ].join(':'),
    )
    .join('|')
}

/** Resolves whichever of `files` aren't already in `cache`, reusing cached content for the rest.
 * Prunes entries no longer referenced by the current revision so `cache` never grows unbounded. */
async function resolveMissing(
  view: EstablishedAhpSessionView,
  files: readonly ChangesetFile[],
  cache: Map<string, ResolvedChangesetFileContent>,
  isCancelled: () => boolean,
): Promise<Map<string, ResolvedChangesetFileContent>> {
  const toResolve = files.filter(file => !cache.has(fileContentKey(file)))
  let nextIndex = 0

  async function worker(): Promise<void> {
    for (;;) {
      if (isCancelled()) return
      const index = nextIndex++
      const file = toResolve[index]
      if (!file) return
      cache.set(fileContentKey(file), await resolveChangesetFileContent(view, file))
    }
  }

  const workerCount = Math.min(MAX_CONCURRENT_READS, toResolve.length)
  await Promise.all(Array.from({length: workerCount}, worker))
  if (isCancelled()) return new Map()

  const results = new Map<string, ResolvedChangesetFileContent>()
  const currentKeys = new Set<string>()
  for (const file of files) {
    const key = fileContentKey(file)
    currentKeys.add(key)
    const resolved = cache.get(key)
    if (resolved) results.set(file.id, resolved)
  }
  // Prune entries the current revision no longer references, so the cache can't grow unbounded.
  for (const key of cache.keys()) {
    if (!currentKeys.has(key)) cache.delete(key)
  }
  return results
}

/** Eagerly resolves before/after content for every file in a changeset (one overall loading state
 * rather than per-row spinners). Only fetches while `enabled` and readResource is supported. */
export function useChangesetDiffContent(
  view: EstablishedAhpSessionView | undefined,
  files: readonly ChangesetFile[],
  enabled: boolean,
): UseChangesetDiffContentResult {
  const token = revisionToken(files)
  const canFetch = enabled && !!view?.readResource && files.length > 0

  // Cache is scoped to one `view` — reset it if the session changes, so stale content can't leak.
  const [cacheOwner, setCacheOwner] = useState(() => ({view, cache: new Map<string, ResolvedChangesetFileContent>()}))
  if (cacheOwner.view !== view) setCacheOwner({view, cache: new Map()})
  const cache = cacheOwner.cache
  const pendingResolutionRef = useRef<{cache: typeof cache; promise: Promise<void>} | undefined>(undefined)

  const [fetchResult, setFetchResult] = useState<{
    view: EstablishedAhpSessionView | undefined
    token: string
    status: ChangesetDiffContentStatus
    contentByFileId: ReadonlyMap<string, ResolvedChangesetFileContent>
  }>({view: undefined, token: '', status: 'loading', contentByFileId: EMPTY_CONTENT})

  useEffect(() => {
    if (!canFetch || !view?.readResource) return
    const activeView = view

    let cancelled = false
    const previousResolution =
      pendingResolutionRef.current?.cache === cache ? pendingResolutionRef.current.promise : Promise.resolve()

    async function run() {
      await previousResolution
      if (cancelled) return
      try {
        const contentByFileId = await resolveMissing(activeView, files, cache, () => cancelled)
        if (cancelled) return
        setFetchResult({view, token, status: 'ready', contentByFileId})
      } catch {
        if (cancelled) return
        setFetchResult({view, token, status: 'error', contentByFileId: EMPTY_CONTENT})
      }
    }
    // Finish in-flight reads before starting the latest revision. Cancelled workers stop
    // scheduling files but still cache completed reads for the next revision.
    pendingResolutionRef.current = {cache, promise: run()}

    return () => {
      cancelled = true
    }
    // `files` omitted: `token` already changes whenever anything relevant about it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, canFetch, view, cache])

  if (!enabled) return IDLE_RESULT
  if (files.length === 0) return READY_EMPTY_RESULT
  if (!canFetch) return {status: 'unavailable', contentByFileId: EMPTY_CONTENT}

  // fetchResult still reflects the previous token while resolving; also compare view since two
  // sessions can coincidentally produce the same token.
  const isCurrent = fetchResult.token === token && fetchResult.view === view
  return isCurrent ? fetchResult : {status: 'loading', contentByFileId: EMPTY_CONTENT}
}
